import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { mensagemDaFuncao } from '@/lib/erroDaFuncao';

interface UseFaturaManualOptions {
  cnpj: string;
  clienteId?: string | null;
  razaoSocial?: string;
  contaFixa?: boolean;
  onSuccess?: (faturaId: string) => void;
}

export function useFaturaManual({ cnpj, clienteId, razaoSocial, contaFixa, onSuccess }: UseFaturaManualOptions) {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  // Mutation para upload + processamento
  const uploadAndProcessMutation = useMutation({
    mutationFn: async (file: File) => {
      const cnpjLimpo = cnpj.replace(/\D/g, '');
      
      // 1. Determinar cliente_id (usar prop ou buscar pelo CNPJ)
      let clienteIdParaUsar = clienteId;
      
      if (!clienteIdParaUsar && cnpj) {
        const { data: clienteData } = await supabase
          .from('clientes')
          .select('id')
          .eq('cnpj', cnpjLimpo)
          .maybeSingle();
        
        clienteIdParaUsar = clienteData?.id || null;
      }

      // 2. Se não encontrou cliente, criar um registro simplificado
      if (!clienteIdParaUsar && cnpjLimpo) {
        console.log('🔧 Cliente não existe, criando registro simplificado...');
        
        const { data: novoCliente, error: createError } = await supabase
          .from('clientes')
          .insert({
            cnpj: cnpjLimpo,
            razao_social: razaoSocial || `Cliente CNPJ ${cnpjLimpo}`,
            classificacao_cliente: 'Fresh',
          })
          .select('id')
          .single();
        
        if (createError) {
          console.error('Erro ao criar cliente:', createError);
          throw new Error(`Não foi possível criar cliente: ${createError.message}`);
        }
        
        clienteIdParaUsar = novoCliente.id;
        console.log('✅ Cliente criado com ID:', clienteIdParaUsar);
        
        // Atualizar o lead com o novo cliente_id
        await supabase
          .from('leads_carteira')
          .update({ cliente_id: novoCliente.id })
          .eq('cnpj', cnpjLimpo);
      }

      // Garantir que temos cliente_id
      if (!clienteIdParaUsar) {
        throw new Error('Não foi possível determinar o cliente para esta fatura');
      }

      // 3. Obter usuário atual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // 4. Upload do arquivo para storage
      const timestamp = Date.now();
      const fileName = `${clienteIdParaUsar}/${timestamp}_${file.name}`;
      
      const { error: uploadError } = await supabase.storage
        .from('faturas')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Erro no upload:', uploadError);
        throw new Error('Falha ao enviar arquivo para o storage');
      }

      // 5. Criar registro em fatura_upload
      const { data: faturaData, error: insertError } = await supabase
        .from('fatura_upload')
        .insert({
          arquivo_url: `faturas/${fileName}`,
          criado_por: user.id,
          cnpj_fatura: cnpjLimpo,
          cliente_id: clienteIdParaUsar,
          origem: 'manual',
          ...(contaFixa ? { metadata: { tipo_conta: 'FIXA' } } : {}),
        })
        .select()
        .single();

      if (insertError) {
        console.error('Erro ao registrar fatura:', insertError);
        throw new Error('Falha ao registrar fatura no banco');
      }

      return faturaData;
    },
    onSuccess: async (faturaData) => {
      toast.success('Fatura enviada! Iniciando análise...');
      
      // 5. Iniciar processamento automático
      setIsProcessing(true);
      
      try {
        // A conta fixa não tem linha de celular: o leitor da móvel a recusava.
        const leitor = contaFixa ? 'ler-conta-fixa-anexada' : 'processar-fatura-claro';
        const { data, error } = await supabase.functions.invoke(leitor, {
          body: { 
            fatura_id: faturaData.id,
            force_refresh: Date.now(),
          },
        });

        // ⛔ Recusa NAO e erro tecnico: o PDF chegou, o leitor nao conseguiu ler,
        // e a importacao nao foi aceita. O consultor precisa ouvir isso com
        // todas as letras — antes o anexo ficava la, vazio, com cara de aceito.
        if (error) {
          const motivo = await mensagemDaFuncao(error, 'Falha ao processar fatura');
          throw new Error(motivo);
        }

        if (data && !data.ok) {
          throw new Error(data.error || data.message || 'Erro na análise da fatura');
        }

        toast.success('Fatura analisada com sucesso!');
        
        // 6. Invalidar todas as queries relevantes
        const cnpjLimpo = cnpj.replace(/\D/g, '');
        queryClient.invalidateQueries({ queryKey: ['fatura-resumo-lead'] });
        queryClient.invalidateQueries({ queryKey: ['fatura-analise-completa', cnpj] });
        queryClient.invalidateQueries({ queryKey: ['ultima-fatura-upload', cnpj] });
        queryClient.invalidateQueries({ queryKey: ['solicitacoes-fatura-cnpj', cnpjLimpo] });
        queryClient.invalidateQueries({ queryKey: ['linhas-fatura', cnpj] });
        queryClient.invalidateQueries({ queryKey: ['faturas'] });
        queryClient.invalidateQueries({ queryKey: ['gestao-leads'] });
        queryClient.invalidateQueries({ queryKey: ['meus-leads-carteira-optimized'] });
        
        onSuccess?.(faturaData.id);
      } catch (err: unknown) {
        console.error('Erro ao processar fatura:', err);
        toast.error('Fatura NÃO importada', {
          description: err instanceof Error ? err.message : 'O sistema não conseguiu ler este PDF.',
          duration: 12000,
        });
        // A ficha tem que voltar a mostrar a fatura boa, nao a recusada.
        queryClient.invalidateQueries({ queryKey: ['ultima-fatura-upload', cnpj] });
        queryClient.invalidateQueries({ queryKey: ['solicitacoes-fatura-cnpj', cnpj.replace(/\D/g, '')] });
      } finally {
        setIsProcessing(false);
      }
    },
    onError: (error: unknown) => {
      console.error('Erro no upload:', error);
      toast.error(`Erro ao enviar fatura: ${error instanceof Error ? error.message : 'Falha ao enviar fatura'}`);
    },
  });

  return {
    uploadAndProcess: uploadAndProcessMutation.mutate,
    uploadAndProcessAsync: uploadAndProcessMutation.mutateAsync,
    isUploading: uploadAndProcessMutation.isPending,
    isProcessing,
    isLoading: uploadAndProcessMutation.isPending || isProcessing,
  };
}
